import React from "react";

import UnitField from "../../common/inputs/UnitField";
import DimensionUtils from "../../common/DimensionUtils";
import { ISizeParamTypeDescriptor } from '../../database/interfaces/paramScript/IParamTypeDescriptor';
import { fieldAccess } from "../../parametersProcessor/paramAccess";
import { boardBoundMax, budgetMax, siblingValues } from "../../parametersProcessor/paramBounds";
import { DatabaseContext } from "../../database/DatabaseHandler";
import { ParametersContext } from "../../parametersProcessor/ParametersProcessor";

/**
 * Size property card content component.
 *
 * MINDEN numerikus beállítás (érték, min, max, lépések, tizedesek) és az egység a descriptorból jön —
 * a feloldás a méret-mezők KÖZÖS komponensében van (`UnitField`), nincs kódba írt méret. Ott dől el a
 * "fix" egység zárolása (min === max, pl. a munkalap-mélység "auto"-ja) és a KERESZT-MEZŐ korlátok
 * (`minFrom` / `maxFrom`) beszűkítése is — pl. hogy a munkalap szélessége ne mehessen a hossza fölé.
 */
interface ISizeCardContentProps {
    typeDescriptor: ISizeParamTypeDescriptor;
    paramValue: string;
    onUpdateParamValue: (value: string) => void;
}

const SizeCardContent: React.FC<ISizeCardContentProps> = ({ typeDescriptor, paramValue, onUpdateParamValue }) => {
    const paramsContext = React.useContext(ParametersContext);
    const dataContext = React.useContext(DatabaseContext);

    // KERET: ha a descriptor megmondja, melyik méretből fogyaszt a mező és kikkel osztozik rajta, a
    // felső határa `keret − a testvérek fogyasztása` — így két zóna magassága nem tudja együtt
    // szétvágni a szekrényt. A meglévő `override` csatornán érkezik (lásd paramBounds `budgetMax`).
    // Két, egymástól független felső korlát futhat ugyanarra a mezőre: a KERET (mennyi maradt a
    // befoglaló méretből) és a TÁBLAMÉRET (mekkora lapból szabják ki). Amelyik szigorúbb, az érvényes.
    const override = React.useMemo(() => {
        const unit = DimensionUtils.parseDimension(String(paramValue ?? "")).unit;
        const limits = [
            budgetMax(
                paramsContext.parameters,
                typeDescriptor.budget,
                unit,
                siblingValues(paramsContext.parameters, typeDescriptor.budget)
            ),
            boardBoundMax(paramsContext.parameters, dataContext.boardData,
                          typeDescriptor.maxFromBoard, unit),
        ].filter((limit): limit is number => limit !== undefined);

        return limits.length ? { max: Math.min(...limits) } : undefined;
    }, [typeDescriptor.budget, typeDescriptor.maxFromBoard, paramValue,
        paramsContext.parameters, dataContext.boardData]);

    return typeDescriptor && (
        <UnitField
            label={typeDescriptor.label}
            tooltip={typeDescriptor.tooltip}
            placeholder={typeDescriptor.placeholder}
            valueStr={paramValue as string}
            units={typeDescriptor.units}
            bounds={typeDescriptor}
            override={override}
            access={fieldAccess(typeDescriptor)}
            // Egységváltásnál az ÚJ egység descriptor-defaultja az érték (ha van) — a Size kártya
            // eddig is így viselkedett.
            unitChange="default"
            onChange={onUpdateParamValue}
        />
    );
}

export default SizeCardContent;
